import React, { useState, useEffect, useRef } from "react";
import { useStaticQuery, graphql } from "gatsby";
import Img from "gatsby-image";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import cx from "clsx";

import ModalCloseButton from "@/components/shared/modalCloseButton";
import ChevronLeft from "@/images/shared/chevron-left.svg";
import ChevronRight from "@/images/shared/chevron-right.svg";
import { debounce } from "@/helpers";

import globalStyles from "@/styles/global.module.scss";
import styles from "./galleryModal.module.scss";

const IMAGES = [
  {
    filename: "ElcoYards_s01_Aerial.png",
    label: "ELCO Yards Aerial, Redwood City"
  },
  {
    filename: "ElcoYards_s02_BldgE.png",
    label: "The Feed and Fuel at E4"
  },
  {
    filename: "ElcoYards_s03_DogPark_B.png",
    label: "Main St. Dog Park at E3"
  },
  { filename: "ElcoYards_s04_LathropSt.png", label: "Lathrop Street Life" },
  {
    filename: "ElcoYards_s05_BuildingBHero.png",
    label: 'Building E1, "The Nest"'
  },
  {
    filename: "ElcoYards_s06_BuildingBInterior.png",
    label: "Shed Interior at Building E1"
  },
  {
    filename: "ElcoYards_s07_BuildingCHero.png",
    label: "The Sawtooth at Building E2"
  },
  {
    filename: "ElcoYards_s08_BuildingENHero.png",
    label: 'Building E3, "The Loft"'
  },
  {
    filename: "ElcoYards_s09_BuildingENInterior.png",
    label: "Workspaces at E3"
  },
  {
    filename: "ElcoYards_s10_BuildingESHero.png",
    label: 'Building E4, "The Mill"'
  }
];

const PrevArrow = ({ currentSlide, slideCount, ...props }) => (
  <button
    {...props}
    className={cx(globalStyles.buttonReset, "slick-prev", "slick-arrow")}
    aria-hidden="true"
    type="button"
  >
    <ChevronLeft alt="prev" className={styles.prevArrow} />
  </button>
);
const NextArrow = ({ currentSlide, slideCount, ...props }) => (
  <button
    {...props}
    className={cx(globalStyles.buttonReset, "slick-next", "slick-arrow")}
    aria-hidden="true"
    type="button"
  >
    <ChevronRight alt="next" className={styles.nextArrow} />
  </button>
);

const GalleryModal = props => {
  const { visible, galleryOpenIndex, images, isDesktop, onClose } = props;

  const gallery = images || IMAGES || [];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentLabel, setCurrentLabel] = useState(gallery[0].label);
  const [padIndex, setPadIndex] = useState(
    String(currentIndex + 1).padStart(2, "0")
  );
  const slickRef = useRef(null);
  const padTotal = String(gallery.length).padStart(2, "0");

  // Change labels between animations
  const changeLabels = debounce(index => {
    const item = gallery[index];
    setCurrentLabel(item?.label);
    setPadIndex(String(index + 1).padStart(2, "0"));
  }, 100);
  const handleBeforeChange = (_, index) => {
    // Start image animation immediately
    setCurrentIndex(index);
    // Change labels after a short delay
    changeLabels(index);
  };
  // Open gallery to specific index
  useEffect(() => {
    if (visible && galleryOpenIndex !== currentIndex) {
      // No animation
      slickRef.current.slickGoTo(galleryOpenIndex, true);
    }
  }, [visible]); // eslint-disable-line

  const data = useStaticQuery(graphql`
    query {
      allFile(
        filter: {
          extension: { regex: "/(jpg)|(png)|(jpeg)/" }
          relativeDirectory: { eq: "gallery" }
        }
      ) {
        edges {
          node {
            base
            childImageSharp {
              fluid(maxWidth: 2000, toFormat: JPG, quality: 90) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
    }
  `);

  return (
    <div className={styles.container}>
      <ModalCloseButton isDesktop={isDesktop} onClick={onClose} />

      <div className={styles.gallery}>
        <Slider
          ref={slickRef}
          dots={false}
          autoplay={false}
          infinite
          arrows={true}
          speed={500}
          centerMode={true}
          centerPadding={0}
          slidesToShow={1}
          slidesToScroll={1}
          nextArrow={isDesktop ? <NextArrow /> : null}
          prevArrow={isDesktop ? <PrevArrow /> : null}
          beforeChange={handleBeforeChange}
        >
          {gallery.map((item, index) => {
            const { filename } = item;
            const image = data.allFile.edges.find(
              img => filename === img.node.base
            );

            if (!image) return null;

            return (
              <div
                key={image.node.childImageSharp.fluid.src}
                className={cx(
                  `${index}`,
                  styles.slide,
                  currentIndex === index && styles.isActive
                )}
              >
                <Img
                  fluid={image.node.childImageSharp.fluid}
                  className={styles.gbimgwrapper}
                  objectFit={"cover"}
                />
              </div>
            );
          })}
        </Slider>
      </div>

      <div className={styles.labelContainer}>
        <div className={styles.label}>{currentLabel}</div>
      </div>
      <div className={styles.positionContainer}>
        <div className={styles.position}>
          <span className={globalStyles.bold}>{padIndex}</span>
          <span className={styles.slash}>/</span>
          {padTotal}
        </div>
      </div>
    </div>
  );
};

export default GalleryModal;
